import "./reverb-module.css";

import { Knob } from "./knob.ts";

// ReverbModule — FDN reverb with three controls. All knobs are unit-
// range [0, 1].
//
// Engine paths:
//   fx.reverb.size      0..1  room size / decay length
//   fx.reverb.damping   0..1  high-frequency absorption per recirculation
//   fx.reverb.mix       0..1  dry/wet

export interface ReverbState {
  size: number;
  damping: number;
  mix: number;
}

export interface ReverbModuleOptions {
  container: HTMLElement;
  setParam: (path: string, value: number) => void;
  initial?: Partial<ReverbState>;
}

export class ReverbModule {
  private root: HTMLDivElement;
  private sizeKnob!: Knob;
  private dampingKnob!: Knob;
  private mixKnob!: Knob;

  constructor(opts: ReverbModuleOptions) {
    const init = opts.initial ?? {};
    const size = init.size ?? 0.55;
    const damping = init.damping ?? 0.4;
    const mix = init.mix ?? 0;

    this.root = document.createElement("div");
    this.root.className = "reverb-module";

    const header = document.createElement("div");
    header.className = "reverb-module-header fx-module-header";
    const title = document.createElement("span");
    title.className = "reverb-module-title fx-module-title";
    title.textContent = "reverb";
    header.append(title);
    this.root.append(header);

    const knobs = document.createElement("div");
    knobs.className = "reverb-module-knobs";
    this.root.append(knobs);

    this.sizeKnob = new Knob({
      container: knobs,
      size: 44,
      min: 0,
      max: 1,
      default: 0.55,
      value: size,
      label: "size",
      format: (v) => `${Math.round(v * 100)}%`,
      onChange: (v) => opts.setParam("fx.reverb.size", v),
    });
    this.dampingKnob = new Knob({
      container: knobs,
      size: 44,
      min: 0,
      max: 1,
      default: 0.4,
      value: damping,
      label: "damping",
      format: (v) => `${Math.round(v * 100)}%`,
      onChange: (v) => opts.setParam("fx.reverb.damping", v),
    });
    this.mixKnob = new Knob({
      container: knobs,
      size: 44,
      min: 0,
      max: 1,
      default: 0,
      value: mix,
      label: "mix",
      format: (v) => `${Math.round(v * 100)}%`,
      onChange: (v) => opts.setParam("fx.reverb.mix", v),
    });

    opts.container.append(this.root);
  }

  getState(): ReverbState {
    return {
      size: this.sizeKnob.current,
      damping: this.dampingKnob.current,
      mix: this.mixKnob.current,
    };
  }

  setState(s: ReverbState): void {
    this.sizeKnob.setValue(s.size);
    this.dampingKnob.setValue(s.damping);
    this.mixKnob.setValue(s.mix);
  }

  destroy(): void {
    this.root.remove();
  }
}
